import React, { useState } from 'react';
import CommentForm from './CommentForm';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

function formatTimeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return date.toLocaleDateString();
}

export default function CommentItem({ comment, user, onReply, onDelete, submitting }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwnComment = user && comment.user.id === user.id;

  const handleReplySubmit = async (content) => {
    await onReply(content);
    setShowReplyForm(false);
  };

  const handleDelete = async () => {
    if (!isOwnComment || deleting) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/v1/comments/${comment.id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': API_KEY,
          'X-User-ID': user.id,
        },
      });

      if (response.ok) {
        onDelete();
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="group">
      {/* Main comment */}
      <div className="flex gap-3">
        {/* Avatar placeholder */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
            {comment.user.username.charAt(0).toUpperCase()}
          </div>
        </div>

        <div className="flex-grow min-w-0">
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm text-gray-900">
                @{comment.user.username}
              </span>
              <span className="text-xs text-gray-500">
                {formatTimeAgo(comment.created_at)}
              </span>
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {comment.content}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
            {user && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="hover:text-indigo-600 transition-colors"
                disabled={submitting}
              >
                Reply
              </button>
            )}
            {isOwnComment && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="hover:text-red-600 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>

          {/* Reply form */}
          {showReplyForm && (
            <div className="mt-2">
              <CommentForm
                onSubmit={handleReplySubmit}
                onCancel={() => setShowReplyForm(false)}
                submitting={submitting}
                placeholder={`Reply to @${comment.user.username}...`}
                user={user}
                isReply={true}
              />
            </div>
          )}

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-3 space-y-2">
              {comment.replies.map((reply) => (
                <div key={reply.id} className="flex gap-2">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                      {reply.user.username.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="bg-gray-100 rounded-lg px-2 py-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-xs text-gray-900">
                          @{reply.user.username}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(reply.created_at)}
                        </span>
                        {user && reply.user.id === user.id && (
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(`${API_BASE}/v1/comments/${reply.id}`, {
                                  method: 'DELETE',
                                  headers: {
                                    'X-API-Key': API_KEY,
                                    'X-User-ID': user.id,
                                  },
                                });
                                if (response.ok) {
                                  // Remove reply from UI - parent handles this
                                  onDelete(reply.id, true, comment.id);
                                }
                              } catch (error) {
                                console.error('Failed to delete reply:', error);
                              }
                            }}
                            className="text-xs text-red-400 hover:text-red-600 ml-auto"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 mt-1">
                        {reply.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}